import Head from "next/head";
import { useRouter } from "next/router";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import Banner, { TBannerLayout } from "../../components/banner";
import { Grid } from "../../components/core/grid";
import { FilterBar } from "../../components/pages/collection/filter_bar";
import { LayoutStateContext } from "../../contexts/layout_state";
import { WindowSizeContext } from "../../contexts/window_size";
import { CollectionsGridItem } from "../../components/pages/collection/grid/item";
import { prebuildData } from "../../lib/prebuild-data";
import { useResizeObserver } from "../../hooks/use_resize_observer";
import { DimensionsStateContext } from "../../contexts/dimensions_state";
import { CollectionFilterStateContext } from "../../contexts/collection_filter_state";
import { CollectionsGridNoItems } from "../../components/pages/collection/grid/no-items";
import { Icon } from "../../components/core/icon";
import { EventPane } from "../../components/core/event_pane";
import { routerTransition } from "../../components/core/router_transition";
import { useDesktopScrollbar } from "../../hooks/use_desktop_scrollbar";
import CollectionNavBar from "../../components/pages/collection/collection-nav-bar";
import { libSearch } from "../../lib/search";
import CollectionSearchBar from "../../components/pages/collection/search-bar";
import { useCustomRouter } from "../../hooks/use_custom_router";
import { getInjectableHeaderTags } from "../../components/layout";
import { useCachedValue } from "../../hooks/use_cached_value";
import { ReviewsHeaderRaw, consumeAllReviewsHeader } from "../../hooks/use_reviews_header";
import CenteredSpinner from "../../components/common/centered-spinner";
import { misc } from "../../lib/misc";
import { BoughtTogetherClient } from "../../hooks/use_bought_together_client";
import { ProductMockReviewCollection } from "../../lib/mock-reviews";

export async function getStaticPaths() {
    prebuildData.loadSnapshot();

    return {
        paths: Object.keys(prebuildData.getCollectionTable()).filter((handle) => handle!=="help").map((id) => ({ params: { id } })),
        fallback: false
    };
}
  
export async function getStaticProps(context) {
    return {
        props: {},
    };
}

export default function Collection() {    
    const reactRouter = useRouter();
    let { id } = reactRouter.query as {[key: string]: string};
    id = id.split("?")[0];

    const { page,mobileDetect: { isActive: isMobile } } = useContext(LayoutStateContext);

    const lastIsMobileRef = useRef(isMobile.current);

    useEffect(() => {
        if (isMobile.current!==lastIsMobileRef.current) {
            lastIsMobileRef.current = isMobile.current;
            page.scrollTo({top: 0,behavior: "smooth"});
        }
    },[isMobile.current]);

    return <CollectionContent key={isMobile.current?1:0} id={id} />;
}

type CollectionContentProps = {
    id: string;
};

function CollectionContent({id}: CollectionContentProps) {
    const filterBarWidth = 138;

    const [filterBarOpen,setFilterBarOpen] = useState(false);
    const [bannerHeightReady,setBannerHeightReady] = useState(false);
    
    
    const { screenRatio,page,misc: { filterBarItemClassNameIdentifier },configuredBannerHeight,mobileDetect: { widthCutoff: mobileCutoff,isActive: isMobile },collectionSearchBarOpen,openSearch,scaleRemToPixel } = useContext(LayoutStateContext);        
    const router = useCustomRouter();

    const windowSize = useContext(WindowSizeContext);
    const { miniNavbarHeight,filterBarControlsHeight,navbarHeight,bannerHeight,collectionGridHeight,scrollBottomPadding } = useContext(DimensionsStateContext);
    const gridFrameRef = useRef<HTMLDivElement>(null);
    const gridVerticalCellSpacing = 20;
    const gridHorizontalCellSpacing: number | { fraction: number } = {fraction: 0.18};
    const targetCellWidth = 350;
    const [gridColumns,setGridColumns] = useState(4);
    const permGridColumns = useRef(gridColumns);
    permGridColumns.current = gridColumns;
    const [gridProcessCounter,setGridProcessCounter] = useState(0);
    const permGridProcessCounter = useRef(gridProcessCounter);
    permGridProcessCounter.current = gridProcessCounter;

    const filterBarBackRef = useRef<HTMLDivElement>();

    const { isSwitching,sortOrder,refreshSwitch,filter } = useContext(CollectionFilterStateContext);

    useEffect(() => {
        return router.beforePushSubscribe((cb) => {
            page.scrollToTopWithCallback(cb);
            return true;
        });
    },[]);

    useEffect(() => {
        if (gridFrameRef.current) {            
            const newGridColumns = (() => 
            {
                if ((typeof gridHorizontalCellSpacing==="object") && (typeof gridHorizontalCellSpacing.fraction==="number"))
                {
                    const f = gridHorizontalCellSpacing.fraction;
                    const t = targetCellWidth;
                    const c = gridFrameRef.current.clientWidth;
                    return Math.max(1,Math.round((c/t+f)/(1+f)));
                }
                else if (typeof gridHorizontalCellSpacing==="number")
                {
                    return Math.max(1,Math.round((gridFrameRef.current.clientWidth+gridHorizontalCellSpacing)/(targetCellWidth+gridHorizontalCellSpacing)));
                }
                else
                {
                    return 3;
                }
            })();
            if (newGridColumns!==permGridColumns.current)
            {
                setGridColumns(newGridColumns);
                setTimeout(() => 
                {
                    setGridProcessCounter(permGridProcessCounter.current+1);
                },0);
            }            
        }                        
    },[`${windowSize.width},${windowSize.height}`,gridFrameRef.current]);

    const [centerControlsInSlider,setCenterControlsInSlider] = useState(windowSize.height>=filterBarControlsHeight.value);
    const [sliderOpen,setSliderOpen] = useState(false);    

    useEffect(() => 
    {
        setCenterControlsInSlider(windowSize.height>=filterBarControlsHeight.value);
    },[`${windowSize.width},${windowSize.height}`,filterBarControlsHeight.value]);

    const timerHandlerRef = useRef(0);    
    const [cancelControlsPointerEvents,setCancelControlsPointerEvents] = useState(false);

    useEffect(() => 
    {
        (window as any).libSearch = libSearch;
        const { current: filterBarBackElem } = filterBarBackRef;
        if (filterBarBackElem)
        {
            const handler = (event: WheelEvent) => 
            {
                if ((event.target instanceof HTMLElement) && (((event.target as HTMLElement).classList.contains(filterBarItemClassNameIdentifier)) || (event.target as HTMLElement).classList.contains("filter-bar-back")))
                {
                    setCancelControlsPointerEvents(true);
                    clearTimeout(timerHandlerRef.current);
                    timerHandlerRef.current = setTimeout(() => 
                    {
                        setCancelControlsPointerEvents(false);
                    },100) as unknown as number;
                }                                
            };
            document.addEventListener("wheel",handler);
            return () => document.removeEventListener("wheel",handler);
        }
    },[]);
    
    const [showFilterBarInline,setShowFilterBarInline] = useState(false);

    useEffect(() => {
        const handlerBeforeRouterScrollUp = () => 
        {
            setShowFilterBarInline(true);
        };
        const handlerAfterRouterScrollUp = () => 
        {
            setShowFilterBarInline(false);
        };

        page.on("before-router-scroll-up",handlerBeforeRouterScrollUp);
        page.on("after-router-scroll-up",handlerAfterRouterScrollUp);

        return () => 
        {
            page.off("before-router-scroll-up",handlerBeforeRouterScrollUp);
            page.off("after-router-scroll-up",handlerAfterRouterScrollUp);    
        };
    },[]);    

    console.log({id});
    
    const collectionItem = prebuildData.getCollectionByHandle(id);    
    const collectionItems = prebuildData.getCollectionItems({omitSupport: true});
    const collectionUrl = useCachedValue(() => collectionItem.route.split("{handle}").join(id),id);
    const reviewsHeader = consumeAllReviewsHeader();

    const refOnGridResize = useResizeObserver(({height}) =>
    {
        collectionGridHeight.set(height);
    });

    let products = collectionItem.products.filter(({hidden}) => !hidden);    

    if (Object.keys(filter.value).length>0) {
        products = products.filter(({filters}) => {
            return (Object.keys(filters).filter((key) => filter.value[key]).length>0);
        });
    }

    if (sortOrder.value==="reviewed") {
        products.sort(({id: aid},{id: bid}) => {
            if (!reviewsHeader) return 0;
            const a = reviewsHeader.products[aid]?.starRating || 0;
            const b = reviewsHeader.products[bid]?.starRating || 0;
            if (a<b) return 1;
            if (a>b) return -1;
            return 0;
        });
    }
    else if (sortOrder.value==="newest") {
        products.sort(({publishedAt: a},{publishedAt: b}) => 
        {
            if (a<b) return 1;
            if (a>b) return -1;
            return 0;
        });
    }

    useEffect(() => {
        setFilterBarOpen(false);
        filter.set({});
    },[id]);

    const gridReadyHandler = () => {
        if (isSwitching.value)
        {
            setTimeout(() => 
            {
                isSwitching.set(false);
                routerTransition.stop();
                const futureScrollMax = (navbarHeight.value+bannerHeight.value+Math.max(collectionGridHeight.value,filterBarControlsHeight.value))-page.clientHeight();
                const targetScrollTop = Math.min(navbarHeight.value+bannerHeight.value-(miniNavbarHeight.value),futureScrollMax);
                if (page.scrollTopMax()<targetScrollTop) page.unlockScroll();
                if (page.scrollTop()>targetScrollTop) 
                {
                    page.scrollToTopWithCallback(() => {
                        page.unlockScroll();
                    });
                    // page.animateScrollTopManaged(targetScrollTop,{},() => 
                    // {
                    //     page.unlockScroll();
                    // });
                }
                else
                {
                    page.unlockScroll();
                }                
            },0);    
        }
    };

    const backClickHandler = () => 
    {
        setSliderOpen(false);
    };

    const openFilterControlsClickHandler = () => 
    {
        setSliderOpen(true);
    };

    const [collectionDropdownMenuOpen,setCollectionDropdownMenuOpen] = useState(false);

    const collectionDropdownClickHandler = () => 
    {
        setCollectionDropdownMenuOpen(true);
        page.hideOverlayScrollbar();
    };

    const collectionDropdownCloseClickHandler = () => 
    {
        setCollectionDropdownMenuOpen(false);
        page.showOverlayScrollbar();
    };    

    const [dropdownMenuHeight,setDropdownMenuHeight] = useState(0);
    const [isDropdownMenuCentered,setIsDropdownMenuCentered] = useState(false);

    const refDropdownMenuOnResize = useResizeObserver(({height}) =>
    {
        setDropdownMenuHeight(height);
    });

    useEffect(() => 
    {
        setIsDropdownMenuCentered(dropdownMenuHeight<=windowSize.height);
    },[windowSize.height,dropdownMenuHeight]);

    const collectionDropdownItemClickHandler = (handle: string) => 
    {
        setCollectionDropdownMenuOpen(false);
        page.showOverlayScrollbar();
        setTimeout(() => 
        {
            const collectionItem = prebuildData.getCollectionByHandle(handle);
            if (collectionItem)
            {
                const route = collectionItem.route.split("{handle}").join(handle);
                router.push(route);
            }            
        },100);
    };

    useLayoutEffect(() => 
    {
        if (refreshSwitch.value===1) 
        {
            setTimeout(() => 
            {
                refreshSwitch.set(2);
            },100);            
        }
        else if (refreshSwitch.value===3) 
        {
            setTimeout(() => 
            {
                refreshSwitch.set(0);
            },50);            
        }
    },[refreshSwitch.value]);

    const { ref: scrollerRef,className: scrollerClassName,injectedStyles: scrollerInjectedStyles,styles: scrollerStyles } = useDesktopScrollbar();        

    const openMobileSearchHandler = () => {        
        page.scrollTo({
            top: scaleRemToPixel(148),
            behavior: "smooth"
        });
        collectionSearchBarOpen.value = true;
        openSearch("collection",collectionItem.id.toString());
    };

    const { collectionBanner: { titleColor,subtitleColor },collectionFilterControls: { backgroundColor: collectionFilterControlsBackgroundColor } } = prebuildData.getActiveTheme()
    
    if (!reviewsHeader) return <div style={{height: "600rem",marginTop: "50rem"}}><CenteredSpinner /></div>

    return (
        <>
            <Head>
                {getInjectableHeaderTags(collectionItem?.seo.title,collectionItem?.seo.description,collectionUrl)}
            </Head>
            <div className={"container"
                +(isMobile.current?" mobile-state":"")
                +(centerControlsInSlider?" center-controls-in-slider":"")
                +(sliderOpen?" slider-open":"")
                +(collectionDropdownMenuOpen?" collection-dropdown-menu-open":"")
                +(isDropdownMenuCentered?" dropdown-menu-centered":"")
                }>                    
                <div className="banner-frame" style={{height: `${(configuredBannerHeight.height!==-1)?configuredBannerHeight.height:270}rem`}}>
                    <Banner                    
                        key={id}
                        src={prebuildData.bustCache(collectionItem?.bannerImageUrl)}
                        aspectRatio={collectionItem?.banner?.image?.ratio?(collectionItem?.banner?.image?.ratio.width/collectionItem?.banner?.image?.ratio.height):586/514} 
                        height={(configuredBannerHeight.height!==-1)?configuredBannerHeight.height:270} 
                        texts={[
                            {
                                color: titleColor,
                                family: "Open Sans",
                                left: (screenRatio>2.1)?-0.2:collectionItem?.banner?.captionLeft,
                                top: 24,
                                size: 25,
                                weight: 500,
                                text: collectionItem?.caption,
                                paddingLeft: 50,
                                paddingRight: 50,
                                letterSpacing: 0.01951
                            },{
                                color: subtitleColor,
                                family: "Open Sans",
                                left: (screenRatio>2.1)?1:collectionItem?.banner?.subCaptionLeft,
                                top: 58,
                                size: 15,
                                weight: 500,
                                text: collectionItem?.subCaption,
                                letterSpacing: 0.0325
                            }
                        ]}
                        layout={TBannerLayout.Left}
                        onImageShowing={() => setFilterBarOpen(true)}
                        onHeightReady={() => setBannerHeightReady(true)}
                        onResize={({height}) => 
                        {
                            bannerHeight.set(height);
                        }}
                    />
                </div>
                <div className="search-bar-wrapper">
                    <CollectionSearchBar collectionId={collectionItem.id.toString()} collectionCaption={collectionItem.caption} />
                </div>
                <div className="mobile-collection-top-bar">                    
                    <CollectionNavBar 
                        leftIconName="collection-filter-open"
                        rightIconName="navbar-search"
                        leftIconClickHandler={openFilterControlsClickHandler}
                        rightIconClickHandler={openMobileSearchHandler}
                        collectionItem={collectionItem}
                        collectionItems={collectionItems}
                    />
                </div>
                <div className="under-banner-spacer"></div>
                <div className="frame">
                    {isMobile.current?(
                        <>
                            <div className="controls-slider-back">
                                <EventPane onClick={backClickHandler} />
                            </div>
                            <div className="collection-dropdown-menu">                            
                                <div className={`scroller ${scrollerClassName}`} ref={scrollerRef} style={scrollerStyles} >
                                    <div ref={refDropdownMenuOnResize} className="menu">
                                        {collectionItems.map(({handle,caption},index) => (
                                            <div key={handle} className={"item"+((handle===id)?" selected":"")}>
                                                {caption}
                                                <EventPane onClick={() => collectionDropdownItemClickHandler(handle)} />
                                            </div>))}
                                    </div>
                                </div>
                                <div className="close-button">
                                    <Icon name="navbar-cross" size={20} />
                                    <EventPane onClick={collectionDropdownCloseClickHandler} />
                                </div>
                            </div>
                        </>
                    ):null}                    
                    <div ref={filterBarBackRef} className="filter-bar-back" style={{opacity: bannerHeightReady?1:0}}>
                        <FilterBar showInline={showFilterBarInline || isMobile.current} cancelControlsPointerEvents={cancelControlsPointerEvents} width={filterBarWidth} open={filterBarOpen} bannerHeightReady={bannerHeightReady} />
                    </div>
                    <div className="on-resize-frame-wrapper">
                        <div ref={refOnGridResize} className="on-resize-frame">
                            <div key={`${id}-${refreshSwitch.value}`} ref={gridFrameRef} className="grid-frame">
                                {((refreshSwitch.value===0) || (refreshSwitch.value===2))?(<>
                                    <Grid 
                                        onReady={gridReadyHandler}
                                        processCounter={gridProcessCounter} 
                                        columns={gridColumns} 
                                        verticalCellSpacing={gridVerticalCellSpacing}
                                        horizontalCellSpacing={gridHorizontalCellSpacing}
                                        scrollAnimation 
                                        scrollAnimationThreshold={0.3}
                                        singleTopMargin={0}>
                                        {products.map((product,index) => {                                                                    
                                            const { caption,smallImages } = product;
                                            const productId = product.id.toString();
                                            const price = prebuildData.getProductPrice(productId,true);                                            
                                            const discountedPrice = prebuildData.getProductPageDiscountedPrice(productId,price).price;
                                            const teaseCaption = ((product.visibility==="tease") && (product.teaseCaption))?product.teaseCaption:undefined;
                                            return <CollectionsGridItem 
                                                key={product.id} 
                                                productId={product.id.toString()} 
                                                caption={caption} 
                                                price={price} 
                                                discountedPrice={discountedPrice} 
                                                imageSrc={prebuildData.bustCache(smallImages[0].src)} 
                                                hoverImageSrc={(smallImages.length>1)?prebuildData.bustCache(smallImages[1].src):undefined} 
                                                teaseCaption={teaseCaption}
                                            />;                                            
                                        })}
                                    </Grid>
                                    {products.length===0?(
                                        <CollectionsGridNoItems />
                                    ):null}                                
                                </>):null}
                            </div>                        
                        </div>
                        <div className="scroll-bottom-padding" style={{ height: (windowSize.width>1300)?scrollBottomPadding.value:0 }}></div>
                    </div>
                </div>
            </div>                        
            <style jsx>{scrollerInjectedStyles}</style>
            <style jsx>{`
            .container {
                position: relative;
            }

            .search-bar-wrapper {
                position: absolute;
                right: 30rem;
                top: 26rem;
            }

            .container.mobile-state {
                --slider-duration: 0.25s;
            }

            .container.mobile-state .filter-bar-back {
                position: fixed;
                left: ${-filterBarWidth}rem;
                top: 0rem;
                width: ${filterBarWidth}rem;
                height: 100%;
                z-index: 103;
                display: flex;
                flex-direction: column;
                justify-content: start;
                overflow: auto;
                transition: transform var(--slider-duration);
            }

            .container.mobile-state.slider-open .filter-bar-back {
                transform: translateX(${filterBarWidth}rem);
            }

            .container.mobile-state.center-controls-in-slider .filter-bar-back {
                justify-content: center;
                overflow: hidden;
            }

            .container.mobile-state .controls-slider-back {
                position: fixed;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;
                background-color: rgba(0,0,0,0.5);
                opacity: 0;
                visibility: hidden;
                z-index: 102;
                transition: visibility var(--slider-duration),opacity var(--slider-duration),background-color 0.25s;
            }

            .container.mobile-state .collection-dropdown-menu {
                position: fixed;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;
                background-color: var(--color-collection-menu);
                z-index: 104;
                opacity: 0;
                visibility: hidden;
                transform: translateY(150rem);
                --duration: 0.25s;
                transition: opacity var(--duration),visibility var(--duration),transform var(--duration);
            }

            .container.mobile-state.collection-dropdown-menu-open .collection-dropdown-menu {
                opacity: 1;
                visibility: visible;
                transform: translateY(0rem);
            }

            .collection-dropdown-menu .scroller {
                position: absolute;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;                
                display: flex;
                flex-direction: column;
                justify-content: start;
                overflow: auto;
            }

            .container.dropdown-menu-centered .collection-dropdown-menu .scroller {
                justify-content: center;
                overflow: hidden;
            }

            .collection-dropdown-menu .scroller .menu {
                padding: 0rem 74rem;
            }

            .collection-dropdown-menu .scroller .item {
                font-size: 20rem;
                font-weight: 500;
                text-align: center;
                padding: 16rem 0rem;
                color: #222;
                position: relative;
                --duration: 0.3s;
                transition: background-color var(--duration),color var(--duration);
            }

            .collection-dropdown-menu .scroller .item.touchdown {
                background-color: rgba(0,0,0,0.02);
            }

            .collection-dropdown-menu .scroller .item.hover {
                color: #777;
            }

            .collection-dropdown-menu .scroller .item.selected.hover {
                color: var(--color-light-blue);
            }

            .collection-dropdown-menu .scroller .item.selected {
                color: var(--color-light-blue);
                font-weight: 600;
            }

            .collection-dropdown-menu .close-button {
                position: absolute;
                right: 5rem;
                top: 0rem;
                color: #888;
                padding: 20rem 25rem;
                --duration: 0.3s;
                transition: background-color var(--duration),color var(--duration);
            }

            .collection-dropdown-menu .close-button.touchdown {
                background-color: rgba(0,0,0,0.06);
            }

            .collection-dropdown-menu .close-button.hover {
                color: #adadad;                
            }

            .container.mobile-state.slider-open .controls-slider-back {
                opacity: 1;
                visibility: visible;
            }

            .container.mobile-state.slider-open .controls-slider-back.touchdown {
                background-color: rgba(0,0,0,0.6);
            }

            .on-resize-frame {
                width: 100%;
            }

            .on-resize-frame-wrapper {
                width: 100%;
            }

            .frame {
                display: flex;
                flex-direction: row;                
            }

            .filter-bar-back {
                flex: 0 0 ${filterBarWidth}rem;
                background-color: ${collectionFilterControlsBackgroundColor};
                display: table-cell;
                position: relative;
                min-height: 900rem;
            }

            .container.mobile-state .filter-bar-back {
                position: fixed;
            }

            .under-banner-spacer {
                height: 0rem;
            }

            .grid-frame {
                padding: 45rem;
                flex-grow: 1;
            }

            .container.mobile-state .grid-frame {
                padding-top: 0rem;
            }

            .collection-top-bar {
                display: none;
                /* padding: 25rem 45rem; */
                padding-left: 20rem;
                padding-right: 25rem;
                padding-top: 20rem;
                padding-bottom: 20rem;
                flex-direction: row;
                align-items: center;
            }

            .filter-open-button {
                padding: 5rem 12rem;
                position: relative;
                transition: background-color 0.2s,color 0.3s;
                color: #333;
                /* background-color: blue; */
            }

            .filter-open-button.touchdown {
                background-color: #eaeaea;
            }


            .filter-open-button.hover {
                color: #999;
            }

            .mobile-collection-top-bar {
                display: none;
            }
            
            @media screen and (max-width: ${mobileCutoff}px) {
                .banner-frame {
                    display: none;
                }

                .collection-top-bar {
                    display: flex;
                }

                .mobile-collection-top-bar {
                    padding-left: 20rem;
                    padding-right: 25rem;
                    padding-top: 20rem;
                    padding-bottom: 20rem;
                    display: block;
                }

                .search-bar-wrapper {
                    /* display: none; */
                    --right: 32rem;
                    top: 25rem;
                    right: var(--right);
                    width: calc(100% - (var(--right) * 2));
                }

                .filter-bar-back {
                    min-height: 0rem;
                }
            }     
            `}</style>
        </>
    );
}